import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { motion } from "framer-motion";
import { Package, Check, Star, Loader2 } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useScrollToTopWhenLoading(loading);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/packages`);
      // Handle paginated response
      setPackages(res.data.packages || res.data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPackageFeatures = (pkg) => {
    const features = [];
    
    if (pkg.credits === 999) {
      features.push("Unlimited classes");
    } else {
      features.push(`${pkg.credits} class${pkg.credits > 1 ? "es" : ""}`);
    }
    
    features.push(`Valid for ${pkg.validity_days} days`);
    
    if (pkg.package_type === "unlimited") {
      features.push("All class types included");
      features.push("Priority booking");
    }
    
    if (pkg.original_price && pkg.original_price > pkg.price) {
      const savings = pkg.original_price - pkg.price;
      features.push(`Save KES ${savings.toLocaleString()}`);
    }
    
    return features;
  };

  return (
    <div className="container-empire" data-testid="packages-page">
      <SEOHead page="packages" />
      {/* Hero */}
      <section className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-cookie text-4xl md:text-5xl text-empire-gold block mb-2">
            Save more, dance more
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Class Packages
          </h1>
          <p className="text-neutral-400 text-lg max-w-xl mx-auto">
            Get the best value with our class packages. More classes, better prices.
          </p>
        </motion.div>
      </section>

      {/* Packages Grid */}
      <section>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-neutral-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No packages available</h3>
            <p className="text-neutral-500">Check back soon for new packages!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-4">
            {packages.map((pkg, index) => {
              const features = getPackageFeatures(pkg);
              const discount = pkg.original_price 
                ? Math.round((1 - pkg.price / pkg.original_price) * 100) 
                : 0;
              
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={pkg.is_featured ? "lg:scale-105 z-10" : ""}
                >
                  <Card className={`h-full relative ${
                    pkg.is_featured 
                      ? "card-empire-featured" 
                      : "card-empire"
                  }`}>
                    {pkg.is_featured && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-empire-gold text-black z-10">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        Most Popular
                      </Badge>
                    )}
                    <CardHeader className="text-center pb-4">
                      
                      <CardTitle className="text-white">{pkg.name}</CardTitle>
                      
                      {pkg.description && (
                        <p className="text-sm text-neutral-400">{pkg.description}</p>
                      )}
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                      {/* Price */}
                      <div className="text-center">
                        {pkg.original_price && pkg.original_price > pkg.price && (
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <span className="text-neutral-500 line-through text-lg">
                              KES {pkg.original_price.toLocaleString()}
                            </span>
                            <Badge className="bg-green-500/20 text-green-400">
                              -{discount}%
                            </Badge>
                          </div>
                        )}
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-empire-gold">
                            KES {pkg.price.toLocaleString()}
                          </span>
                        </div>
                        {pkg.credits !== 999 && (
                          <p className="text-sm text-neutral-500 mt-1">
                            KES {Math.round(pkg.price / pkg.credits).toLocaleString()} per class
                          </p>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-3">
                        {features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-neutral-300">
                            <Check className="w-5 h-5 text-empire-gold flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Link to={`/packages/${pkg.slug}`} className="block">
                        <Button 
                          className={`w-full ${
                            pkg.is_featured ? "btn-empire" : "btn-empire-outline"
                          }`}
                          data-testid={`buy-package-${pkg.slug}`}
                        >
                          Buy Package
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* FAQ Section */}
      <section className="mt-16">
        <div className="glass rounded-2xl p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-white mb-2">How do packages work?</h3>
              <p className="text-neutral-400 text-sm">
                Purchase a package and receive credits that can be used to book any available class. 
                Credits are valid for the specified number of days from purchase.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">Can I share my package?</h3>
              <p className="text-neutral-400 text-sm">
                Packages are non-transferable and tied to the email used during purchase.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">What happens if my package expires?</h3>
              <p className="text-neutral-400 text-sm">
                Unused credits expire at the end of the validity period and cannot be refunded or extended.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
